/**
 * Realcraft Horizon - Main Application Bundle
 * 
 * This file serves as the entry point for:
 * - TailwindCSS (imported via CSS)
 * - Alpine.js with plugins
 * - Global Alpine data stores and components
 * 
 * Built with Vite and output to assets/app.js
 */

// Import Tailwind CSS
import './app.css';

// Import Alpine.js core
import Alpine from 'alpinejs';

// Import Alpine.js plugins
import focus from '@alpinejs/focus';
import collapse from '@alpinejs/collapse';

// ========================================
// ALPINE.JS PLUGIN REGISTRATION
// ========================================

// Register plugins before starting Alpine
Alpine.plugin(focus);
Alpine.plugin(collapse);

// ========================================
// CART HELPERS (shared by Alpine kit blocks)
// ========================================

/**
 * Returns the unique section IDs of all cart-items components present on the page.
 * Used to render-and-return updated cart section HTML in a single Cart API call.
 */
function collectCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.add(item.dataset.sectionId);
    }
  });
  return Array.from(ids);
}

/**
 * Fetches the current cart from Shopify for use as `resource` in the
 * Horizon-native `cart:update` event payload.
 */
async function fetchCart() {
  const url = window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : '/cart.js';
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

// ========================================
// GLOBAL ALPINE DATA STORES
// ========================================

/**
 * Theme Store
 * Manages global theme state (dark mode, cart, etc.)
 */
document.addEventListener('alpine:init', () => {
  Alpine.store('theme', {
    // Cart state
    cart: {
      itemCount: 0,
      open: false,
    },
    
    // Mobile menu state
    mobileMenu: {
      open: false,
    },
    
    // Search modal
    search: {
      open: false,
    },
    
    // Current breakpoint detection
    breakpoint: 'desktop',
    
    // Initialize theme store
    init() {
      this.detectBreakpoint();
      window.addEventListener('resize', () => this.detectBreakpoint());
    },
    
    detectBreakpoint() {
      const width = window.innerWidth;
      if (width < 640) this.breakpoint = 'mobile';
      else if (width < 768) this.breakpoint = 'sm';
      else if (width < 1024) this.breakpoint = 'md';
      else if (width < 1280) this.breakpoint = 'lg';
      else this.breakpoint = 'xl';
    },
    
    toggleCart() {
      this.cart.open = !this.cart.open;
    },
    
    toggleMobileMenu() {
      this.mobileMenu.open = !this.mobileMenu.open;
    },
    
    toggleSearch() {
      this.search.open = !this.search.open;
    },
  });

  /**
   * Mega Menu Store
   * Manages mega menu state for desktop and mobile
   */
  Alpine.store('megaMenu', {
    // Desktop: which mega menu is currently active (by parent link handle)
    activeMegaMenu: null,
    
    // Mobile: which accordion item is open
    activeMobileAccordion: null,
    
    // Toggle mega menu on desktop (click-based)
    toggle(parentLinkHandle) {
      this.activeMegaMenu = this.activeMegaMenu === parentLinkHandle ? null : parentLinkHandle;
    },
    
    // Check if a mega menu is active
    isActive(parentLinkHandle) {
      return this.activeMegaMenu === parentLinkHandle;
    },
    
    // Toggle mobile accordion
    toggleMobileAccordion(parentLinkHandle) {
      this.activeMobileAccordion = this.activeMobileAccordion === parentLinkHandle ? null : parentLinkHandle;
    },
    
    // Check if mobile accordion is open
    isMobileAccordionOpen(parentLinkHandle) {
      return this.activeMobileAccordion === parentLinkHandle;
    },
    
    // Close all mega menus
    closeAll() {
      this.activeMegaMenu = null;
      this.activeMobileAccordion = null;
    }
  });
});

// ========================================
// ALPINE DATA COMPONENTS
// ========================================

document.addEventListener('alpine:init', () => {
  
  /**
   * Dropdown Component
   * Reusable dropdown with focus management
   */
  Alpine.data('dropdown', () => ({
    open: false,
    
    toggle() {
      this.open = !this.open;
    },
    
    close() {
      this.open = false;
    },
    
    // Close on escape key
    init() {
      this.$watch('open', value => {
        if (value) {
          this.$nextTick(() => this.$refs.trigger?.focus());
        }
      });
    },
  }));
  
  /**
   * Accordion Component
   * Expandable/collapsible sections
   */
  Alpine.data('accordion', (initialOpen = false) => ({
    open: initialOpen,
    
    toggle() {
      this.open = !this.open;
    },
    
    close() {
      this.open = false;
    },
  }));
  
  /**
   * Modal Component
   * Accessible modal dialog
   */
  Alpine.data('modal', () => ({
    open: false,
    
    init() {
      // Close on escape
      this.$el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.open) {
          this.close();
        }
      });
    },
    
    openModal() {
      this.open = true;
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    },
    
    close() {
      this.open = false;
      document.body.style.overflow = '';
    },
    
    // Close on backdrop click
    backdropClose(e) {
      if (e.target === this.$el) {
        this.close();
      }
    },
  }));
  
  /**
   * Tab Component
   * Tab panel interface
   */
  Alpine.data('tabs', (activeTab = 0) => ({
    activeTab: activeTab,
    
    setTab(index) {
      this.activeTab = index;
    },
    
    isActive(index) {
      return this.activeTab === index;
    },
  }));
  
  /**
   * Quantity Selector Component
   * Product quantity input with increment/decrement
   */
  Alpine.data('quantitySelector', (initial = 1, min = 1, max = 99) => ({
    quantity: initial,
    min: min,
    max: max,
    
    init() {
      // Ensure initial value is within bounds
      this.quantity = Math.max(this.min, Math.min(this.max, this.quantity));
    },
    
    increment() {
      if (this.quantity < this.max) {
        this.quantity++;
        this.$dispatch('quantity-change', this.quantity);
      }
    },
    
    decrement() {
      if (this.quantity > this.min) {
        this.quantity--;
        this.$dispatch('quantity-change', this.quantity);
      }
    },
    
    validate() {
      let value = parseInt(this.quantity);
      if (isNaN(value) || value < this.min) value = this.min;
      if (value > this.max) value = this.max;
      this.quantity = value;
      this.$dispatch('quantity-change', this.quantity);
    },
  }));
  
  /**
   * Product Card Component
   * Quick add and hover effects for product cards
   */
  Alpine.data('productCard', () => ({
    hover: false,
    quickAddOpen: false,
    
    init() {
      // Add intersection observer for lazy loading if needed
    },
    
    onHover() {
      this.hover = true;
    },
    
    onLeave() {
      this.hover = false;
      this.quickAddOpen = false;
    },
    
    toggleQuickAdd() {
      this.quickAddOpen = !this.quickAddOpen;
    },
  }));
  
  /**
   * Slideshow Component
   * Carousel/slideshow functionality
   */
  Alpine.data('slideshow', (autoplay = false, interval = 5000) => ({
    currentSlide: 0,
    totalSlides: 0,
    autoplay: autoplay,
    interval: interval,
    timer: null,
    
    init() {
      // Count slides
      this.totalSlides = this.$el.querySelectorAll('[data-slide]').length;
      
      if (this.autoplay) {
        this.startAutoplay();
      }
    },
    
    next() {
      this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
      this.resetAutoplay();
    },
    
    prev() {
      this.currentSlide = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
      this.resetAutoplay();
    },
    
    goTo(index) {
      this.currentSlide = index;
      this.resetAutoplay();
    },
    
    startAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = setInterval(() => this.next(), this.interval);
    },
    
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    
    resetAutoplay() {
      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    },
    
    destroy() {
      this.stopAutoplay();
    },
  }));
  
  /**
   * Sticky Header Component
   * Header that sticks on scroll
   */
  Alpine.data('stickyHeader', () => ({
    isSticky: false,
    lastScrollY: 0,
    isHidden: false,
    
    init() {
      this.handleScroll();
      window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    },
    
    handleScroll() {
      const scrollY = window.scrollY;
      const threshold = 100; // pixels to scroll before sticking
      
      // Toggle sticky state
      this.isSticky = scrollY > threshold;
      
      // Hide/show on scroll direction (optional)
      if (scrollY > this.lastScrollY && scrollY > 200) {
        this.isHidden = true;
      } else {
        this.isHidden = false;
      }
      
      this.lastScrollY = scrollY;
    },
  }));
  
  /**
   * Form Validation Component
   * Client-side form validation
   */
  Alpine.data('formValidation', () => ({
    errors: {},
    touched: {},
    
    validate(field, rules) {
      const value = this.$el.querySelector(`[name="${field}"]`).value;
      this.errors[field] = [];
      
      if (rules.required && !value) {
        this.errors[field].push('This field is required');
      }
      
      if (rules.email && value && !this.isValidEmail(value)) {
        this.errors[field].push('Please enter a valid email address');
      }
      
      if (rules.minLength && value.length < rules.minLength) {
        this.errors[field].push(`Must be at least ${rules.minLength} characters`);
      }
      
      return this.errors[field].length === 0;
    },
    
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    
    hasError(field) {
      return this.touched[field] && this.errors[field]?.length > 0;
    },
    
    getErrors(field) {
      return this.errors[field] || [];
    },
    
    markTouched(field) {
      this.touched[field] = true;
    },
  }));
  
  /**
   * Mega Menu Component
   * Manages mega menu dropdown state
   */
  Alpine.data('megaMenu', () => ({
    activeMenu: null,
    closeTimer: null,
    
    init() {
      // Listen for other menus opening
      window.addEventListener('mega-menu:open', (e) => {
        if (e.detail !== this.activeMenu) {
          this.activeMenu = e.detail;
        }
      });
      
      window.addEventListener('mega-menu:close-all', () => {
        this.close();
      });
    },
    
    open(menuId) {
      this.clearCloseTimer();
      this.activeMenu = menuId;
      window.dispatchEvent(new CustomEvent('mega-menu:open', { detail: menuId }));
    },
    
    close() {
      this.activeMenu = null;
    },
    
    delayedClose(delay = 150) {
      this.closeTimer = setTimeout(() => this.close(), delay);
    },
    
    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    },
    
    isOpen(menuId) {
      return this.activeMenu === menuId;
    },
    
    toggle(menuId) {
      if (this.activeMenu === menuId) {
        this.close();
      } else {
        this.open(menuId);
      }
    },
  }));
  
  // Liquid sometimes serializes Image drops as a plain URL string (e.g. when
  // using `image | json`). Normalize to the `{id, src, alt, width, height}`
  // shape the rest of the kit-line code expects.
  const normalizeKitLineImage = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'string') {
      const src = raw.startsWith('//') ? `https:${raw}` : raw;
      return { id: null, src, alt: '', width: 0, height: 0 };
    }
    if (typeof raw === 'object') {
      const src = raw.src
        ? (raw.src.startsWith('//') ? `https:${raw.src}` : raw.src)
        : '';
      if (!src) return null;
      return {
        id: raw.id ?? null,
        src,
        alt: raw.alt ?? '',
        width: raw.width ?? 0,
        height: raw.height ?? 0,
      };
    }
    return null;
  };

  /**
   * Product Kit Line Component
   * Single-product line used inside the product page kit builder.
   * Mirrors `sections/reclaimed-wood-samples.liquid` cart wiring so the
   * Horizon cart drawer / count receive the canonical `cart:update` event
   * with `resource` (cart) and `data.sections` payload after each add.
   */
  Alpine.data('productKitLine', (config = {}) => ({
    productId: config.productId ?? null,
    productHandle: config.productHandle ?? '',
    productTitle: config.productTitle ?? '',
    productUrl: config.productUrl ?? '',
    available: !!config.available,
    hasVariants: !!config.hasVariants,
    variants: Array.isArray(config.variants) ? config.variants : [],
    productSelector: !!config.productSelector,
    products: Array.isArray(config.products) ? config.products : [],
    selectedProductIndex: 0,
    productSelectPoller: null,
    minQuantity: Math.max(1, Number(config.minQuantity) || 1),
    maxQuantity: Math.max(1, Number(config.maxQuantity) || 99),
    quantity: Math.max(Number(config.defaultQuantity) || 1, Number(config.minQuantity) || 1),
    selectedByDefault: !!config.selectedByDefault,
    blockId: config.blockId ?? '',
    moneyFormat: config.moneyFormat || '${{amount}}',
    currencyCode: config.currencyCode || 'USD',
    selectedVariantId: config.defaultVariantId ?? null,
    selectedVariantPrice: Number(config.defaultPrice) || 0,
    selectedVariantAvailable: !!config.defaultAvailable,
    selectedVariantImage: normalizeKitLineImage(config.defaultImage),
    productFeaturedImage: normalizeKitLineImage(config.productFeaturedImage),
    drivesPrimaryMedia: !!config.drivesPrimaryMedia,
    editorPreviewAtc: config.editorPreviewAtc !== false,
    helpOpen: false,
    adding: false,
    error: '',
    success: '',
    selected: false,
    summaryOnPage: false,
    soldOutLabel: config.soldOutLabel || 'Sold Out',
    unavailableLabel: config.unavailableLabel || 'Unavailable',
    summaryAddLabel: config.summaryAddLabel || 'Add',
    summaryAddedLabel: config.summaryAddedLabel || 'Added',
    stepKey: '',
    requiredFlag: false,

    init() {
      // Capture data-* attributes once into reactive data fields. Reading
      // this.$el.dataset inside getters can return stale results when Alpine
      // re-evaluates bindings, so we hoist them at init time.
      this.stepKey = this.$el?.dataset?.stepKey || '';
      this.requiredFlag = this.$el?.dataset?.required === 'true';
      if (this.selectedByDefault || this.requiredFlag) this.selected = true;
      // Detect a sibling product-kit-summary block on the page. When present,
      // accessory kit-lines route their primary action to "add to summary"
      // (toggle `selected`) instead of POSTing directly to the cart.
      this.summaryOnPage = !!document.querySelector('[data-product-kit-summary]');
      if (this.productSelector && this.products.length > 0) {
        this.syncSelectedProduct(0);
        this.$nextTick(() => {
          this.productSelectPoller = window.setInterval(() => {
            if (!this.$refs.productSelect) return;
            if (this.$refs.productSelect.value !== this.productHandle) {
              this.onProductChange();
            }
          }, 250);
        });
      }
      this.$nextTick(() => this.broadcastState());
      // Recompute selected variant after first paint to honor the variant picker's defaults.
      if (this.hasVariants) {
        this.$nextTick(() => this.onOptionChange());
      }
    },

    destroy() {
      if (this.productSelectPoller) {
        window.clearInterval(this.productSelectPoller);
      }
    },

    get isEditorPreview() {
      return this.editorPreviewAtc && typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode === true;
    },

    get canAddToCart() {
      if (this.isEditorPreview) return true;
      return this.available && this.selectedVariantAvailable && this.selectedVariantId !== null;
    },

    get realCanAddToCart() {
      return this.available && this.selectedVariantAvailable && this.selectedVariantId !== null;
    },

    get formattedUnitPrice() {
      if (this.selectedVariantId === null) return this.unavailableLabel;
      if (!this.selectedVariantAvailable) return this.soldOutLabel;
      return this.formatMoney(this.selectedVariantPrice);
    },

    get statusLabel() {
      if (this.selectedVariantId === null) return this.unavailableLabel;
      if (!this.selectedVariantAvailable) return this.soldOutLabel;
      return '';
    },

    get displayImageSrc() {
      const image = this.selectedVariantImage || this.productFeaturedImage;
      if (!image || !image.src) return '';
      const sep = image.src.includes('?') ? '&' : '?';
      return `${image.src}${sep}width=600`;
    },

    get isAccessoryLine() {
      return typeof this.stepKey === 'string' && this.stepKey.indexOf('accessory-') === 0;
    },

    // True when this line should hand its primary action off to the summary
    // (accessory + summary block exists on the page). Required steps always
    // remain "selected" and are not user-toggleable from the line.
    get summaryMode() {
      return this.summaryOnPage && this.isAccessoryLine && !this.requiredFlag;
    },

    get summaryToggleLabel() {
      return this.selected
        ? (this.summaryAddedLabel || 'Added')
        : (this.summaryAddLabel || 'Add');
    },

    get optionSummaryParts() {
      const parts = [];
      const optionRefPrefix = this.productSelector ? `productOption${this.selectedProductIndex}_` : 'option';
      let index = 0;
      while (this.$refs[`${optionRefPrefix}${index}`]) {
        const select = this.$refs[`${optionRefPrefix}${index}`];
        const label = select
          .closest('.product-kit-line__variant-label')
          ?.querySelector('.product-kit-line__variant-name')
          ?.textContent
          ?.trim();
        const value = select.value || select.selectedOptions?.[0]?.textContent?.trim() || '';
        if (label && value) parts.push(`${label}: ${value}`);
        index += 1;
      }
      return parts;
    },

    get summaryTitle() {
      if (this.optionSummaryParts.length === 0) return this.productTitle;
      if (this.optionSummaryParts.length === 1) return this.optionSummaryParts[0];
      return [this.productTitle, ...this.optionSummaryParts].filter(Boolean).join(' | ');
    },

    toggleSelected() {
      if (!this.realCanAddToCart) return;
      this.selected = !this.selected;
      this.broadcastState();
    },

    increment() {
      if (this.quantity < this.maxQuantity) {
        this.quantity = Number(this.quantity) + 1;
        this.broadcastState();
      }
    },

    decrement() {
      if (this.quantity > this.minQuantity) {
        this.quantity = Number(this.quantity) - 1;
        this.broadcastState();
      }
    },

    validateQuantity() {
      let value = parseInt(this.quantity, 10);
      if (Number.isNaN(value) || value < this.minQuantity) value = this.minQuantity;
      if (value > this.maxQuantity) value = this.maxQuantity;
      this.quantity = value;
      this.broadcastState();
    },

    syncSelectedProduct(index) {
      const selectedProduct = this.products[index];
      if (!selectedProduct) return;

      this.selectedProductIndex = index;
      this.productId = selectedProduct.id ?? null;
      this.productHandle = selectedProduct.handle ?? '';
      this.productTitle = selectedProduct.title ?? '';
      this.productUrl = selectedProduct.url ?? '';
      this.available = !!selectedProduct.available;
      this.hasVariants = !!selectedProduct.hasVariants;
      this.variants = Array.isArray(selectedProduct.variants) ? selectedProduct.variants : [];
      this.selectedVariantId = selectedProduct.defaultVariantId ?? null;
      this.selectedVariantPrice = Number(selectedProduct.defaultPrice) || 0;
      this.selectedVariantAvailable = !!selectedProduct.defaultAvailable;
      this.productFeaturedImage = normalizeKitLineImage(selectedProduct.featuredImage);
      this.selectedVariantImage = this.resolveVariantImage(this.selectedVariantId);
    },

    resolveVariantImage(variantId) {
      if (variantId == null) return this.productFeaturedImage || null;
      const variant = this.variants.find((v) => v && v.id === variantId);
      const normalized = normalizeKitLineImage(variant && variant.featured_image);
      return normalized || this.productFeaturedImage || null;
    },

    onProductChange() {
      if (!this.productSelector || !this.$refs.productSelect) return;
      const selectedHandle = this.$refs.productSelect.value;
      const selectedIndex = this.products.findIndex((product) => product.handle === selectedHandle);
      this.syncSelectedProduct(selectedIndex >= 0 ? selectedIndex : this.$refs.productSelect.selectedIndex);
      this.error = '';
      this.success = '';
      this.$nextTick(() => {
        if (this.hasVariants) {
          this.onOptionChange();
        } else {
          this.broadcastState();
        }
      });
    },

    onOptionChange() {
      if (!this.hasVariants) return;
      // Read each option select by ref order and look up the matching variant.
      const optionValues = [];
      let i = 0;
      const optionRefPrefix = this.productSelector ? `productOption${this.selectedProductIndex}_` : 'option';
      while (this.$refs[`${optionRefPrefix}${i}`]) {
        optionValues.push(this.$refs[`${optionRefPrefix}${i}`].value);
        i++;
      }

      const match = this.variants.find((variant) => {
        const variantOptions = variant.options || [];
        return optionValues.every((value, index) => variantOptions[index] === value);
      });

      if (match) {
        this.selectedVariantId = match.id;
        this.selectedVariantPrice = match.price;
        this.selectedVariantAvailable = !!match.available;
        this.selectedVariantImage = normalizeKitLineImage(match.featured_image)
          || this.productFeaturedImage;
      } else {
        this.selectedVariantId = null;
        this.selectedVariantPrice = 0;
        this.selectedVariantAvailable = false;
      }

      // When this kit-line drives the primary product media, forward the selected
      // option values into a sibling hidden native <variant-picker>. That picker
      // fetches the host product section at ?option_values=... and dispatches the
      // native VariantUpdateEvent — Horizon's MediaGallery / price / SKU / inventory
      // components react automatically. No custom swap code needed.
      if (this.drivesPrimaryMedia && !this.productSelector) {
        this.forwardToHiddenPicker(optionValues);
      }

      this.broadcastState();
    },

    forwardToHiddenPicker(optionValues) {
      // Query document-wide; native <variant-picker> may morph itself on each
      // change so caching $el-relative refs is fragile.
      const picker = document.querySelector(
        `variant-picker[data-kit-line-block-id="${this.blockId}"]`
      );
      if (!picker) return;
      const fieldsets = picker.querySelectorAll('fieldset[data-fieldset-index]');
      let lastChangedRadio = null;
      fieldsets.forEach((fieldset, index) => {
        const desiredValue = optionValues[index];
        if (desiredValue == null) return;
        let radio = null;
        try {
          radio = fieldset.querySelector(`input[value="${CSS.escape(desiredValue)}"]`);
        } catch (_err) {
          radio = null;
        }
        if (!radio) {
          radio = Array.from(fieldset.querySelectorAll('input')).find(r => r.value === desiredValue) || null;
        }
        if (!radio || radio.checked) return;
        radio.checked = true;
        lastChangedRadio = radio;
      });
      if (lastChangedRadio) {
        lastChangedRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const dollars = Math.floor(amount);
      const fractional = Math.round((amount - dollars) * 100).toString().padStart(2, '0');

      const replacements = {
        '{{amount}}': `${dollars.toLocaleString('en-US')}.${fractional}`,
        '{{amount_no_decimals}}': dollars.toLocaleString('en-US'),
        '{{amount_with_comma_separator}}': `${dollars
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${fractional}`,
        '{{amount_no_decimals_with_comma_separator}}': dollars
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      };

      let formatted = this.moneyFormat;
      for (const [token, value] of Object.entries(replacements)) {
        formatted = formatted.replaceAll(token, value);
      }
      return formatted;
    },

    get cheapestVariantPrice() {
      if (!Array.isArray(this.variants) || this.variants.length === 0) {
        return Number(this.selectedVariantPrice) || 0;
      }
      let cheapest = Infinity;
      for (const variant of this.variants) {
        const price = Number(variant && variant.price);
        if (!Number.isFinite(price)) continue;
        if (price < cheapest) cheapest = price;
      }
      return cheapest === Infinity ? Number(this.selectedVariantPrice) || 0 : cheapest;
    },

    broadcastState() {
      const detail = {
        blockId: this.blockId,
        productId: this.productId,
        productHandle: this.productHandle,
        title: this.productTitle,
        summaryTitle: this.summaryTitle,
        optionSummary: this.optionSummaryParts,
        variantId: this.selectedVariantId,
        unitPrice: this.selectedVariantPrice,
        cheapestPrice: this.cheapestVariantPrice,
        quantity: Number(this.quantity) || 0,
        lineTotal: (Number(this.selectedVariantPrice) || 0) * (Number(this.quantity) || 0),
        canAdd: this.realCanAddToCart,
        statusLabel: this.statusLabel,
        required: this.requiredFlag,
        stepKey: this.stepKey || '',
        selected: this.selected,
        variantImage: this.selectedVariantImage,
      };
      document.dispatchEvent(
        new CustomEvent('product-kit-line:state', { bubbles: true, detail })
      );
      // Note: primary-media swapping is handled natively. When drivesPrimaryMedia
      // is true, onOptionChange() forwards selected values into a hidden native
      // <variant-picker> (rendered alongside this kit-line), which triggers the
      // standard Horizon variant-update flow that re-renders <media-gallery>.
    },

    onCartUpdate(event) {
      // External cart update (e.g. cart drawer remove). Keep button state in sync if needed.
      const cart = event?.detail?.resource;
      if (!cart || !Array.isArray(cart.items)) return;
      // No-op for now; future enhancement could surface "in cart" badges per line.
    },

    async addToCart() {
      if (this.isEditorPreview && !this.realCanAddToCart) return;
      if (!this.canAddToCart || this.adding) return;
      if (this.selectedVariantId === null) {
        this.error = 'Please choose an option to continue.';
        return;
      }

      this.adding = true;
      this.error = '';
      this.success = '';

      try {
        const formData = new FormData();
        formData.append('id', String(this.selectedVariantId));
        formData.append('quantity', String(this.quantity));

        const sectionIds = collectCartSectionIds();
        if (sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
        }
        formData.append('sections_url', window.location.pathname);

        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const responseText = await response.text();
        let payload = {};
        if (responseText.trim() !== '') {
          try {
            payload = JSON.parse(responseText);
          } catch (parseError) {
            console.warn('Unable to parse kit-line add response.', parseError);
          }
        }

        if (payload.status) {
          this.error = payload.description || payload.message || 'Unable to add this item to the cart.';
          document.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: {
                sourceId: `product-kit-line-${this.blockId}`,
                data: {
                  message: payload.message,
                  description: payload.description,
                  errors: payload.errors,
                },
              },
            })
          );
          return;
        }

        const cart = await fetchCart();
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: `product-kit-line-${this.blockId}`,
              data: {
                source: 'product-form-component',
                itemCount: Number(this.quantity),
                productId: this.productId ? String(this.productId) : undefined,
                variantId: String(this.selectedVariantId),
                sections: payload.sections,
              },
            },
          })
        );

        this.success = 'Added to cart.';
        setTimeout(() => {
          this.success = '';
        }, 4000);
      } catch (err) {
        console.error(err);
        this.error = 'Unable to add this item to the cart.';
      } finally {
        this.adding = false;
      }
    },
  }));

  /**
   * Product Kit Total Component
   * Subscribes to `product-kit-line:state` events and exposes a single total
   * for the kit. Initial render shows "From $X" computed from the cheapest
   * variant of every required line (no accessories). Once the customer makes
   * any change, switches to the live total of the currently selected
   * required + accessory lines.
   */
  Alpine.data('productKitTotal', (config = {}) => ({
    blockId: config.blockId ?? '',
    moneyFormat: config.moneyFormat || '${{amount}}',
    fromLabel: config.fromLabel || 'From',
    totalLabel: config.totalLabel || '',
    includeAccessoriesInTotal: config.includeAccessoriesInTotal !== false,
    lines: {},
    interacted: false,
    quantityChangeBaseline: {},

    init() {
      document.addEventListener('product-kit-line:state', (event) => {
        const detail = event && event.detail ? event.detail : null;
        if (!detail || !detail.blockId) return;

        const previous = this.lines[detail.blockId];
        const next = {
          required: !!detail.required,
          stepKey: detail.stepKey || '',
          unitPrice: Number(detail.unitPrice) || 0,
          cheapestPrice: Number(detail.cheapestPrice) || Number(detail.unitPrice) || 0,
          quantity: Number(detail.quantity) || 0,
          lineTotal: Number(detail.lineTotal) || 0,
          variantId: detail.variantId,
          canAdd: !!detail.canAdd,
          selected: !!detail.selected,
        };

        if (previous) {
          // Anything that's not a first registration counts as an interaction
          // when the variant id, quantity, or line total has actually moved.
          if (
            previous.variantId !== next.variantId ||
            previous.selected !== next.selected ||
            previous.canAdd !== next.canAdd ||
            previous.quantity !== next.quantity ||
            previous.lineTotal !== next.lineTotal
          ) {
            this.interacted = true;
          }
        }

        this.lines[detail.blockId] = next;
      });
    },

    get fromPrice() {
      let total = 0;
      for (const line of Object.values(this.lines)) {
        if (!line.required) continue;
        const qty = Math.max(1, line.quantity || 1);
        total += (Number(line.cheapestPrice) || 0) * qty;
      }
      return total;
    },

    get livePrice() {
      let total = 0;
      for (const line of Object.values(this.lines)) {
        const isAccessory = line.stepKey && line.stepKey.indexOf('accessory-') === 0;
        if (isAccessory && !this.includeAccessoriesInTotal) continue;
        if (!line.canAdd) continue;
        if (!line.required && !(isAccessory && line.selected)) continue;
        total += Number(line.lineTotal) || 0;
      }
      return total;
    },

    get displayPrice() {
      return this.interacted ? this.livePrice : this.fromPrice;
    },

    get displayLabel() {
      if (!this.interacted) return this.fromLabel;
      return this.totalLabel || '';
    },

    get formattedPrice() {
      return this.formatMoney(this.displayPrice);
    },

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const dollars = Math.floor(amount);
      const fractional = Math.round((amount - dollars) * 100).toString().padStart(2, '0');

      const replacements = {
        '{{amount}}': `${dollars.toLocaleString('en-US')}.${fractional}`,
        '{{amount_no_decimals}}': dollars.toLocaleString('en-US'),
        '{{amount_with_comma_separator}}': `${dollars
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${fractional}`,
        '{{amount_no_decimals_with_comma_separator}}': dollars
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      };

      let formatted = this.moneyFormat;
      for (const [token, value] of Object.entries(replacements)) {
        formatted = formatted.replaceAll(token, value);
      }
      return formatted;
    },
  }));

  /**
   * Product Kit Summary Component
   * Aggregates sibling product-kit-line states and posts a single batched
   * `/cart/add.js` request with all selected items, then dispatches the
   * Horizon-native `cart:update` event so the drawer/count refresh once.
   */
  Alpine.data('productKitSummary', (config = {}) => ({
    blockId: config.blockId ?? '',
    moneyFormat: config.moneyFormat || '${{amount}}',
    requireAllRequired: config.requireAllRequired !== false,
    missingRequiredLabel: config.missingRequiredLabel || 'Please choose all required options to continue.',
    cartUrl: config.cartUrl || '/cart',
    lines: {},
    adding: false,
    error: '',
    success: '',

    init() {
      document.addEventListener('product-kit-line:state', (event) => {
        const detail = event.detail || {};
        if (!detail.blockId) return;
        this.lines[detail.blockId] = detail;
      });
    },

    get linesArray() {
      return Object.values(this.lines);
    },

    get selectedLines() {
      // Required kit steps opt in automatically; optional accessories only join
      // the batch summary when a merchant explicitly pre-selects them. Keep
      // unavailable selections visible so the customer can see what must change.
      return this.linesArray.filter(
        (line) => line.selected && Number(line.quantity) > 0
      );
    },

    get addableSelectedLines() {
      return this.selectedLines.filter((line) => line.canAdd);
    },

    isAccessoryLine(line) {
      return !!(line && typeof line.stepKey === 'string' && line.stepKey.indexOf('accessory-') === 0);
    },

    get requiredLines() {
      return this.selectedLines.filter((line) => !this.isAccessoryLine(line));
    },

    get accessoryLines() {
      return this.selectedLines.filter((line) => this.isAccessoryLine(line));
    },

    get hasSelection() {
      return this.selectedLines.length > 0;
    },

    get hasAccessoryLines() {
      return this.accessoryLines.length > 0;
    },

    get hasRequiredLines() {
      return this.requiredLines.length > 0;
    },

    get totalPrice() {
      return this.selectedLines.reduce(
        (sum, line) => line.canAdd ? sum + (Number(line.lineTotal) || 0) : sum,
        0
      );
    },

    get requiredStepGroups() {
      const groups = new Map();
      for (const line of this.linesArray) {
        if (!line.required) continue;
        const key = line.stepKey || line.blockId;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            label: this.stepLabel(line),
            hasAddableLine: false,
          });
        }
        const group = groups.get(key);
        if (line.canAdd && Number(line.quantity) > 0) {
          group.hasAddableLine = true;
        }
      }
      return Array.from(groups.values());
    },

    get missingRequired() {
      if (!this.requireAllRequired) return [];
      return this.requiredStepGroups.filter((group) => !group.hasAddableLine);
    },

    get missingRequiredMessage() {
      if (this.missingRequired.length === 0) return '';
      const labels = this.missingRequired.map((group) => group.label).filter(Boolean);
      if (labels.length === 0) return this.missingRequiredLabel;
      return `Please add ${this.humanList(labels)} before checkout.`;
    },

    get canSubmit() {
      if (this.adding) return false;
      if (!this.hasSelection) return false;
      if (this.requireAllRequired && this.missingRequired.length > 0) return false;
      return true;
    },

    linePriceLabel(line) {
      if (!line || !line.canAdd) return line?.statusLabel || 'Unavailable';
      return this.formatMoney(line.lineTotal);
    },

    stepLabel(line) {
      const key = line?.stepKey || '';
      const match = key.match(/^step-(\d+)$/);
      if (match) {
        return `Step ${this.numberWord(Number(match[1]))}`;
      }
      return line?.title || 'this step';
    },

    numberWord(value) {
      const words = [
        'Zero',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine',
        'Ten',
      ];
      return words[value] || String(value);
    },

    humanList(items) {
      const uniqueItems = [...new Set(items)];
      if (uniqueItems.length <= 1) return uniqueItems[0] || '';
      if (uniqueItems.length === 2) return `${uniqueItems[0]} and ${uniqueItems[1]}`;
      return `${uniqueItems.slice(0, -1).join(', ')}, and ${uniqueItems[uniqueItems.length - 1]}`;
    },

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const dollars = Math.floor(amount);
      const fractional = Math.round((amount - dollars) * 100).toString().padStart(2, '0');
      const tokens = {
        '{{amount}}': `${dollars.toLocaleString('en-US')}.${fractional}`,
        '{{amount_no_decimals}}': dollars.toLocaleString('en-US'),
        '{{amount_with_comma_separator}}': `${dollars
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${fractional}`,
        '{{amount_no_decimals_with_comma_separator}}': dollars
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      };
      let formatted = this.moneyFormat;
      for (const [token, value] of Object.entries(tokens)) {
        formatted = formatted.replaceAll(token, value);
      }
      return formatted;
    },

    onCartUpdate(event) {
      // No-op placeholder for future "items in cart" reconciliation.
      void event;
    },

    async addAllToCart() {
      if (!this.canSubmit) return;
      const items = this.addableSelectedLines.map((line) => ({
        id: line.variantId,
        quantity: Number(line.quantity),
      }));
      if (items.length === 0) return;

      this.adding = true;
      this.error = '';
      this.success = '';

      try {
        const sectionIds = collectCartSectionIds();
        const body = {
          items,
        };
        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok || payload.status) {
          this.error = payload.description || payload.message || 'Unable to add items to the cart.';
          document.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: {
                sourceId: `product-kit-summary-${this.blockId}`,
                data: {
                  message: payload.message,
                  description: payload.description,
                  errors: payload.errors,
                },
              },
            })
          );
          return;
        }

        const cart = await fetchCart();
        const totalCount = items.reduce((sum, item) => sum + item.quantity, 0);

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: `product-kit-summary-${this.blockId}`,
              data: {
                source: 'product-form-component',
                itemCount: totalCount,
                sections: payload.sections,
              },
            },
          })
        );

        this.success = 'Items added to your cart.';
        setTimeout(() => {
          this.success = '';
        }, 4000);
      } catch (err) {
        console.error(err);
        this.error = 'Unable to add items to the cart.';
      } finally {
        this.adding = false;
      }
    },
  }));

  /**
   * Cart Drawer Component
   * Handles cart drawer interactions
   */
  Alpine.data('cartDrawer', () => ({
    loading: false,
    error: null,
    
    async updateQuantity(key, quantity) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity: quantity,
          }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to update cart');
        }
        
        const cart = await response.json();
        
        // Dispatch event to update cart UI
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        
        // Refresh the cart drawer content
        this.refreshCart();
        
      } catch (err) {
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },
    
    async removeItem(key) {
      await this.updateQuantity(key, 0);
    },
    
    async refreshCart() {
      try {
        const response = await fetch('/?sections=header-actions', {
          method: 'GET',
        });
        
        if (response.ok) {
          const sections = await response.json();
          // Update the cart drawer content
          if (sections['header-actions']) {
            const temp = document.createElement('div');
            temp.innerHTML = sections['header-actions'];
            const newCartContent = temp.querySelector('.cart-drawer__inner');
            const currentCartContent = document.querySelector('.cart-drawer__inner');
            if (newCartContent && currentCartContent) {
              currentCartContent.innerHTML = newCartContent.innerHTML;
            }
          }
        }
      } catch (err) {
        // Fallback to page refresh
        window.location.reload();
      }
    },
  }));
  
});

// ========================================
// GLOBAL ALPINE UTILITIES (MAGIC PROPERTIES)
// ========================================

document.addEventListener('alpine:init', () => {
  
  /**
   * $screen magic property
   * Check current breakpoint
   * Usage: x-show="$screen('md')"
   */
  Alpine.magic('screen', () => (breakpoint) => {
    const width = window.innerWidth;
    const breakpoints = {
      'mobile': width < 640,
      'sm': width >= 640,
      'md': width >= 768,
      'lg': width >= 1024,
      'xl': width >= 1280,
      '2xl': width >= 1536,
    };
    return breakpoints[breakpoint] || false;
  });
  
  /**
   * $delay magic property
   * Delay execution
   * Usage: @click="$delay(300).then(() => doSomething())"
   */
  Alpine.magic('delay', () => (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  });
  
});

// ========================================
// INITIALIZE ALPINE
// ========================================

// Expose Alpine globally so inline Liquid section scripts can register
// Alpine.data()/stores during production theme builds as well.
window.Alpine = Alpine;

// Start Alpine.js
Alpine.start();

// ========================================
// CONSOLE INFO
// ========================================

console.log('%cRealcraft Horizon', 'font-size: 20px; font-weight: bold; color: #000;');
console.log('%cTailwindCSS v4 + Alpine.js v3', 'font-size: 12px; color: #666;');
console.log('%cReady for development ✨', 'font-size: 12px; color: #00c853;');
